import React from "react";

/**
 * FilterToolbar — the strip that carries a list page's inline search and its
 * Filters trigger. The first half of the FilterPanel pattern (D3).
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 * The three `/sa/*` pages migrated in Phase 6.3 all needed the same two controls in
 * the same strip, so the rule went into `sa-dashboard.scss` as `.sa-toolbar` rather
 * than being written three times. That was right at three pages and wrong at thirty:
 * `.sa-toolbar` lives in a `/sa/*` stylesheet, so an institute-level list page cannot
 * use it, and the alternative is a per-page rule at every one of them — which is
 * exactly how the 40-entry prefix registry in TOKEN_CONTRACT §9 came about.
 *
 * So it is a component, in utilities, with no stylesheet. `.sa-toolbar` stays for now
 * because `sa-dashboard.scss` is still SCSS and its pages still import it; it goes
 * when that sheet converts.
 *
 * ── Layout ───────────────────────────────────────────────────────────────────
 * Search takes the remaining width and collapses to a 240px minimum before the row
 * wraps; the trigger and any actions keep their natural size. Search is FIRST and
 * stays on the page deliberately — it is the control people reach for on arrival, and
 * burying it behind a button costs a click on every visit (Phase 6.3).
 *
 * Usage:
 *   <FilterToolbar
 *     search={<SearchInput value={q} onChange={setQ} placeholder="Search students…" />}
 *     filters={<FilterButton count={f.count} onClick={() => f.setShow(true)} />}
 *     actions={<AppButton onClick={onAdd}>Add Student</AppButton>}
 *   />
 *
 * Props:
 *   search    — the search control (rendered first, flexes)
 *   filters   — the Filters trigger (usually <FilterButton />)
 *   actions   — optional right-aligned page actions
 *   children  — anything else, placed between filters and actions
 *   flush     — drop the bottom margin, for a strip that already sits inside a
 *               padded container: `AppDataTable`'s `toolbar` bar. The margin
 *               below is spacing between this strip and a SEPARATE table; in a
 *               card header the container's padding is that spacing, and the
 *               margin only pushes the strip off-centre in its own bar.
 *   className — extra classes on the strip
 *
 * ⚠️ The bottom margin is INTERNAL, and that is not a style preference. Most of the
 * thirty pages migrating onto this pattern still import their own SCSS, so a `tw-`
 * class written at the call site would put them in the mixed state §6.1 forbids and
 * fail `lint:tokens --check mixed`. A shared component is Tailwind; the pages using
 * it are not, yet. Spacing therefore has to live here until they convert. It is the
 * `.filter-bar { padding: 0.5rem 0 }` this replaces, in the one place that can hold
 * it. Override with `className` if a page genuinely needs something else.
 */
const FilterToolbar = ({ search, filters, actions, children, flush = false, className = "" }) => (
  /* ⚠️ `flush` REMOVES the class rather than overriding it. `cn()` here is a
     plain join, not a merge, so a `tw-mb-0` appended after `tw-mb-3` would be
     two utilities on one element decided by emitted source order — and
     Tailwind emits `mb-0` BEFORE `mb-3`, so the override would lose. */
  <div
    className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 ${flush ? "" : "tw-mb-3"} ${className}`
      .replace(/\s+/g, " ")
      .trim()}
  >
    {search && (
      <div className="tw-flex-[1_1_240px] tw-min-w-0">{search}</div>
    )}
    {filters}
    {children}
    {/* `ml-auto` only when there is a search box to push away from — without one,
        actions would otherwise jump to the far right of an otherwise empty row.
        The actions wrap among themselves, so on a phone a third button drops to
        a new line instead of running off the card. */}
    {actions && (
      <div className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 ${search ? "" : "tw-ml-auto"}`}>
        {actions}
      </div>
    )}
  </div>
);

export default FilterToolbar;
